import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';

import { MatchScores, Matchup, MatchupSponsorInterface, PodMatch, Team, TeamLeagueMatchup } from '../types/ticker.types';

// Streaming live URLs can carry a transient `t` timestamp param — drop it (mirrors formatTickerMatches).
const removeTParam = (url: string): string => {
	try {
		const parsed = new URL(url);
		parsed.searchParams.delete('t');
		return parsed.toString();
	} catch {
		return url;
	}
};

function convertTo12HourWithMinutes(time24: string): string {
	const [hourStr, minuteStr] = time24.split(':');
	if (!hourStr) return '';
	const hour = parseInt(hourStr, 10);
	const hour12 = hour % 12 === 0 ? 12 : hour % 12;
	const suffix = hour < 12 ? 'AM' : 'PM';
	return `${hour12}:${minuteStr} ${suffix}`;
}

// NEW: plannedStartDate is UTC; utcOffset is seconds (can be +/-). Return local time "h:mm AM/PM".
function getLocalStartTime12h(plannedStartDate?: string | null, utcOffsetSeconds?: number | null): string {
	if (!plannedStartDate) return '';

	const baseMs = Date.parse(plannedStartDate);
	if (Number.isNaN(baseMs)) return '';

	const offsetMs = (utcOffsetSeconds ?? 0) * 1000;
	const local = new Date(baseMs + offsetMs);

	const hh = String(local.getUTCHours()).padStart(2, '0');
	const mm = String(local.getUTCMinutes()).padStart(2, '0');

	return convertTo12HourWithMinutes(`${hh}:${mm}`);
}

const convertToPodMatches = (scoresMap: Record<string, MatchScores[]>, winMap: Record<string, number>): PodMatch[] =>
	Object.entries(scoresMap).map(([podUuid, matches]) => ({
		podUuid,
		podTitle: matches[0]?.podTitle || null,
		matches,
		matchesWon: winMap[podUuid] || 0
	}));

const sortByPodTitle = (a: PodMatch, b: PodMatch) => {
	if (a.podTitle && b.podTitle) return a.podTitle.localeCompare(b.podTitle);
	if (a.podTitle) return -1;
	if (b.podTitle) return 1;
	return 0;
};

export interface FormatTLTickerMatchupsProps {
	matchupsInfos: TeamLeagueMatchup[];
	teamLeagueSlug: string;
	ptlUrl: string;
	placeholderImage: string;
}

export function formatTLTickerMatchups({ matchupsInfos, teamLeagueSlug, ptlUrl, placeholderImage }: FormatTLTickerMatchupsProps): Matchup[] {
	if (matchupsInfos.length === 0) return [];

	const matchups: Matchup[] = matchupsInfos
		.filter((matchUpInfo) => matchUpInfo.teamOneAbbreviation && matchUpInfo.teamTwoAbbreviation)
		.map((matchupInfo) => {
			// CHANGED: startTime comes from matchupInfo.plannedStartDate (UTC) + matchupInfo.timezone.utcOffset (seconds)
			const startTime = getLocalStartTime12h(matchupInfo.plannedStartDate, +matchupInfo.timezone?.utcOffset);

			// Matchup-level links (the matchup itself holds none). matchupStatsURL is the
			// matchup results page; live/archived come from the first match that has one.
			const matchupStatsURL = `${ptlUrl}/team-leagues/${teamLeagueSlug}/matchup/${matchupInfo.uuid}`;

			// Resolve each TV link independently across the matchup's matches (they can come
			// from different matches): liveURL from the first in-progress/assigned-to-court
			// match that has one; archivedURL/highlightsURL from the first completed match that has each.
			const matchStreamingServices = matchupInfo.matches.map((match) => ({
				match,
				streamingService: match.streamingServices?.[0]
			}));

			const liveEntry = matchStreamingServices.find(
				({ match, streamingService }) =>
					((match.localDateMatchAssignedToCourt && !match.localDateMatchCompleted) || match.matchStatus === 2) && streamingService?.liveUrl
			);
			const archivedEntry = matchStreamingServices.find(
				({ match, streamingService }) => match.matchStatus === 4 && streamingService?.archivedUrl
			);
			const highlightsEntry = matchStreamingServices.find(
				({ match, streamingService }) => match.matchStatus === 4 && streamingService?.highlightsUrl
			);

			const liveURL = liveEntry?.streamingService?.liveUrl ? removeTParam(liveEntry.streamingService.liveUrl) : undefined;
			const archivedURL = archivedEntry?.streamingService?.archivedUrl || undefined;
			const highlightsURL = highlightsEntry?.streamingService?.highlightsUrl || undefined;

			const status: Matchup['status'] =
				matchupInfo.matchupStatus === 'COMPLETED_MATCHUP_STATUS'
					? 'COMPLETED'
					: matchupInfo.matchupStatus === 'IN_PROGRESS_MATCHUP_STATUS'
						? 'LIVE'
						: matchupInfo.matchupStatus === 'SCHEDULED_MATCHUP_STATUS'
							? 'SCHEDULED'
							: 'DELAYED';

			// Network logo, mapped the same way formatTickerMatches builds Match.sponsors for
			// MatchCardV2: CDN-resized to 100x100 and desaturated once the matchup is final.
			// Prefer the service behind the link we actually surfaced so the logo always matches
			// the stream; fall back to the first match that carries a logo at all.
			const logoStreamingService = [liveEntry, archivedEntry, highlightsEntry, ...matchStreamingServices]
				.map((entry) => entry?.streamingService)
				.find((streamingService) => streamingService?.logoUrl);

			const saturation = status === 'COMPLETED' ? -100 : 0;

			const sponsors: MatchupSponsorInterface | undefined =
				logoStreamingService && logoStreamingService.logoUrl
					? {
							logoUrl: getCDNURL(logoStreamingService.logoUrl, 100, 100, saturation),
							serviceId: logoStreamingService.serviceId,
							serviceName: logoStreamingService.serviceName
						}
					: undefined;

			const teamOnePodScores: Record<string, MatchScores[]> = {};
			const teamTwoPodScores: Record<string, MatchScores[]> = {};
			const teamOnePodMatchWins: Record<string, number> = {};
			const teamTwoPodMatchWins: Record<string, number> = {};

			let matchLabels: string[] = [];

			// A column is a division pod (a "line"), and the number of lines now varies by
			// division (CSP: 3 Premier / 2 Masters / 1 Prime). Only matches that actually carry
			// a division pod may become a column — a matchup's conditional tiebreaker carries
			// none and used to fall into a synthetic bucket, inflating every card by one column.
			// Legacy formats where NO match carries a pod (single-bucket MLP) keep the old
			// behaviour: every match stays in one bucket and the card renders game scores.
			const podScopedMatches = matchupInfo.matches.filter((match) => match.teamLeagueSeasonDivisionPodUuid);
			const displayedMatches = podScopedMatches.length > 0 ? podScopedMatches : matchupInfo.matches;

			displayedMatches.forEach((match) => {
				const podUuid = match.teamLeagueSeasonDivisionPodUuid ?? 'D';
				const podTitle = match.teamLeagueSeasonDivisionPodTitle ?? null;
				const scoreFormatGameBestOutOf = match.scoreFormatGameBestOutOf || match.scoreFormatMaxGames;
				const minPointsToWin = match.scoreFormatGame1Max || 11;
				const scoreFormatGame1WinBy = match.scoreFormatGame1WinBy || 2;

				const teamOneGameScores = [match.teamOneGameOneScore || 0];
				const teamTwoGameScores = [match.teamTwoGameOneScore || 0];

				matchLabels.push(match.matchAbbreviation || '');

				let teamOneGamesWon = 0;
				let teamTwoGamesWon = 0;

				// GAME 1
				if (match.teamOneGameOneScore >= minPointsToWin && match.teamOneGameOneScore - match.teamTwoGameOneScore >= scoreFormatGame1WinBy) {
					teamOneGamesWon++;
				} else if (
					match.teamTwoGameOneScore >= minPointsToWin &&
					match.teamTwoGameOneScore - match.teamOneGameOneScore >= scoreFormatGame1WinBy
				) {
					teamTwoGamesWon++;
				}

				// GAME 2
				if (scoreFormatGameBestOutOf > 1) {
					teamOneGameScores.push(match.teamOneGameTwoScore || 0);
					teamTwoGameScores.push(match.teamTwoGameTwoScore || 0);

					if (
						match.teamOneGameTwoScore >= minPointsToWin &&
						match.teamOneGameTwoScore - match.teamTwoGameTwoScore >= scoreFormatGame1WinBy
					) {
						teamOneGamesWon++;
					} else if (
						match.teamTwoGameTwoScore >= minPointsToWin &&
						match.teamTwoGameTwoScore - match.teamOneGameTwoScore >= scoreFormatGame1WinBy
					) {
						teamTwoGamesWon++;
					}
				}

				// GAME 3
				if (scoreFormatGameBestOutOf > 2) {
					teamOneGameScores.push(match.teamOneGameThreeScore || 0);
					teamTwoGameScores.push(match.teamTwoGameThreeScore || 0);

					if (
						match.teamOneGameThreeScore >= minPointsToWin &&
						match.teamOneGameThreeScore - match.teamTwoGameThreeScore >= scoreFormatGame1WinBy
					) {
						teamOneGamesWon++;
					} else if (
						match.teamTwoGameThreeScore >= minPointsToWin &&
						match.teamTwoGameThreeScore - match.teamOneGameThreeScore >= scoreFormatGame1WinBy
					) {
						teamTwoGamesWon++;
					}
				}

				// Determine match winner
				if (teamOneGamesWon > teamTwoGamesWon) {
					teamOnePodMatchWins[podUuid] = (teamOnePodMatchWins[podUuid] || 0) + 1;
				} else if (teamTwoGamesWon > teamOneGamesWon) {
					teamTwoPodMatchWins[podUuid] = (teamTwoPodMatchWins[podUuid] || 0) + 1;
				}

				// Store game scores per pod
				if (!teamOnePodScores[podUuid]) teamOnePodScores[podUuid] = [];
				if (!teamTwoPodScores[podUuid]) teamTwoPodScores[podUuid] = [];

				teamOnePodScores[podUuid].push({
					podUuid,
					podTitle,
					uuid: match.matchUuid,
					gameScores: [...teamOneGameScores],
					minPointsToWin,
					scoreFormatGame1WinBy
				});

				teamTwoPodScores[podUuid].push({
					podUuid,
					podTitle,
					uuid: match.matchUuid,
					gameScores: [...teamTwoGameScores],
					minPointsToWin,
					scoreFormatGame1WinBy
				});
			});

			const teamOnePodMatches = convertToPodMatches(teamOnePodScores, teamOnePodMatchWins).sort(sortByPodTitle);
			const teamTwoPodMatches = convertToPodMatches(teamTwoPodScores, teamTwoPodMatchWins).sort(sortByPodTitle);

			const teamOne: Team = {
				logo: getCDNURL(matchupInfo.teamOneLogo?.url || placeholderImage, 48, 48),
				name: matchupInfo.teamOneAbbreviation,
				podMatches: teamOnePodMatches
			};

			const teamTwo: Team = {
				logo: getCDNURL(matchupInfo.teamTwoLogo?.url || placeholderImage, 48, 48),
				name: matchupInfo.teamTwoAbbreviation,
				podMatches: teamTwoPodMatches
			};

			const multiplePodsMatches = teamOne.podMatches.length > 1;

			if (multiplePodsMatches) {
				// Label from the pod itself. This used to special-case index 3 as the dreambreaker,
				// which only held while every division ran exactly 3 pods — with 2 lines the
				// tiebreaker bucket landed at index 2 and rendered as "Pod 3".
				matchLabels = teamOne.podMatches.map((podMatch, index) => podMatch.podTitle || `Pod ${index + 1}`);
			} else if (matchLabels.length === 5 && matchLabels.length > matchLabels.map((matchLabel) => matchLabel !== '').length) {
				matchLabels = ['W', 'M', 'MX1', 'MX2', 'D'];
			}

			const matchupObject: Matchup = {
				uuid: matchupInfo.uuid,
				time: startTime,
				timezone: matchupInfo.matches[0]?.timezoneAbbreviation || '',
				winner: matchupInfo.winner === 1 ? 'team1' : matchupInfo.winner === 2 ? 'team2' : undefined,
				status,
				matchLabels,
				teamOne,
				teamTwo,
				links: {
					matchupStatsURL,
					matchupStatsURLTarget: '_self',
					liveURL,
					liveURLTarget: '_blank',
					archivedURL,
					archivedURLTarget: '_blank',
					highlightsURL,
					highlightsURLTarget: '_blank'
				},
				sponsors
			};

			return matchupObject;
		});

	return matchups;
}
